import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageLayout from '@/components/layout/PageLayout';
import SeoHead from '@/components/seo/SeoHead';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import { CONTACT } from '@/config/constants';
import { submitContactForm } from '@/services/api';

const demoRequestSchema = z.object({
  name: z.string().min(2, 'Please enter your full name'),
  email: z.string().email('Please enter a valid work email address'),
  role: z.string().min(1, 'Please select your role'),
  organisation: z.string().min(2, 'Please enter your centre or service name'),
  teamSize: z.string().optional(),
  enquiryType: z.string().min(1, 'Please select an enquiry type'),
  notes: z.string().optional(),
});

type DemoRequestFormData = z.infer<typeof demoRequestSchema>;

const WHAT_TO_EXPECT = [
  "We'll review your enquiry within 1 business day",
  "We'll reach out to confirm a time that suits you",
  'Demo runs 30–45 minutes, tailored to your service',
  'Pilot programs available for services ready to evaluate',
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DemoRequestFormData>({
    resolver: zodResolver(demoRequestSchema),
  });

  const onSubmit = async (data: DemoRequestFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await submitContactForm({
        name: data.name,
        email: data.email,
        organisation: data.organisation,
        message: [
          `Role: ${data.role}`,
          `Enquiry: ${data.enquiryType}`,
          `Team size: ${data.teamSize || 'Not specified'}`,
          data.notes ? `\nNotes:\n${data.notes}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      });
      setSubmitted(true);
      reset();
      setTimeout(() => setSubmitted(false), 8000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <SeoHead
        title="Book a Demo — Notematic Clinical Documentation for Mental Health Centres | Australia"
        description="Request a demo of Notematic's AI-assisted IAR-DST documentation platform for Medicare Mental Health Centres. 30–45 minute tailored session. Pilot programs available."
        canonical="/contact"
      />
      <div className="section-padding bg-white dark:bg-secondary-900">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-4">
                Book a Demo or Request a Pilot
              </h1>
              <p className="text-lg text-secondary-600 dark:text-secondary-400 max-w-2xl mx-auto">
                Tell us about your centre or service and we'll be in touch within one business day
                to arrange a tailored walkthrough.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left panel */}
              <div className="lg:col-span-1 space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                    What to expect
                  </h3>
                  <ul className="space-y-3">
                    {WHAT_TO_EXPECT.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-sm text-secondary-600 dark:text-secondary-400"
                      >
                        <svg
                          className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-base font-semibold text-secondary-900 dark:text-white mb-2">
                    Prefer to reach us directly?
                  </h3>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                  >
                    {CONTACT.email}
                  </a>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-2">
                <div className="card p-8">
                  {submitted ? (
                    <div className="text-center py-12 animate-fade-in">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-success-100 dark:bg-success-950 rounded-full mb-4">
                        <svg
                          className="w-8 h-8 text-success-600 dark:text-success-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold text-secondary-900 dark:text-white mb-2">
                        Enquiry received
                      </h3>
                      <p className="text-secondary-600 dark:text-secondary-400">
                        Thank you — we'll be in touch within one business day to follow up.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Input
                          {...register('name')}
                          id="name"
                          label="Full name"
                          placeholder="Your name"
                          error={errors.name?.message}
                          required
                          disabled={isSubmitting}
                        />
                        <Input
                          {...register('email')}
                          id="email"
                          type="email"
                          label="Work email"
                          placeholder="you@yourcentre.com.au"
                          error={errors.email?.message}
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="w-full">
                          <label htmlFor="role" className="label">
                            Your role <span className="text-error-500 ml-1">*</span>
                          </label>
                          <select
                            {...register('role')}
                            id="role"
                            className="input"
                            disabled={isSubmitting}
                          >
                            <option value="">Select your role</option>
                            <option value="Practice Manager">Practice Manager</option>
                            <option value="Clinical Director">Clinical Director</option>
                            <option value="Clinician">Clinician</option>
                            <option value="CEO / Owner">CEO / Owner</option>
                            <option value="Other">Other</option>
                          </select>
                          {errors.role && (
                            <p className="mt-1 text-sm text-error-500">{errors.role.message}</p>
                          )}
                        </div>

                        <div className="w-full">
                          <label htmlFor="teamSize" className="label">
                            Number of clinicians
                          </label>
                          <select
                            {...register('teamSize')}
                            id="teamSize"
                            className="input"
                            disabled={isSubmitting}
                          >
                            <option value="">Select team size</option>
                            <option value="1–5">1–5</option>
                            <option value="6–15">6–15</option>
                            <option value="16–50">16–50</option>
                            <option value="50+">50+</option>
                          </select>
                        </div>
                      </div>

                      <Input
                        {...register('organisation')}
                        id="organisation"
                        label="Centre / Service name"
                        placeholder="Your organisation"
                        error={errors.organisation?.message}
                        required
                        disabled={isSubmitting}
                      />

                      <div className="w-full">
                        <label htmlFor="enquiryType" className="label">
                          I'd like to <span className="text-error-500 ml-1">*</span>
                        </label>
                        <select
                          {...register('enquiryType')}
                          id="enquiryType"
                          className="input"
                          disabled={isSubmitting}
                        >
                          <option value="">Select an option</option>
                          <option value="Book Demo">Book a demo</option>
                          <option value="Request Pilot">Request a pilot program</option>
                          <option value="General Enquiry">General enquiry</option>
                        </select>
                        {errors.enquiryType && (
                          <p className="mt-1 text-sm text-error-500">{errors.enquiryType.message}</p>
                        )}
                      </div>

                      <Textarea
                        {...register('notes')}
                        id="notes"
                        label="Anything else we should know?"
                        placeholder="Optional — context about your service, current tools, or specific questions"
                        rows={4}
                        disabled={isSubmitting}
                      />

                      {error && (
                        <div className="p-4 bg-error-50 dark:bg-error-950 border border-error-200 dark:border-error-800 rounded-lg">
                          <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
                        </div>
                      )}

                      <Button type="submit" size="lg" fullWidth disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                      </Button>

                      <p className="text-center text-xs text-secondary-500 dark:text-secondary-400">
                        Privacy-first enquiry handling. We will never share your details.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

